/**
 * ParallelForkInspector — Phase 6b-3.
 *
 * Splits execution into N parallel branches. Source schema:
 * soarNodes.js:2268 — single `branches` field of type json. The BE accepts
 * an array of `{id, label}` objects; the canonical port set is
 * branch1/branch2/branch3 unless overridden.
 *
 * V3 used a raw JSON textarea here. V4's Inspector replaces that with a
 * structured row-editor (id + label) so authors can't fat-finger the
 * shape. Empty `branches` arrays fall back to the 3-default port set on
 * the BE.
 *
 * Spread-preservation contract per useCanvas:1098.
 */

import React from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';

export default function ParallelForkInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const branches = Array.isArray(cfg.branches) ? cfg.branches : [];

  const setBranches = (next) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, branches: next } });
  };

  const addBranch = () => {
    if (readOnly) return;
    const nextIdx = branches.length + 1;
    setBranches([
      ...branches,
      { id: `branch${nextIdx}`, label: `Branch ${nextIdx}` },
    ]);
  };
  const removeBranch = (i) => {
    if (readOnly) return;
    setBranches(branches.filter((_, idx) => idx !== i));
  };
  const updateBranch = (i, key, value) => {
    if (readOnly) return;
    const next = branches.map((b, idx) => (idx === i ? { ...b, [key]: value } : b));
    setBranches(next);
  };
  const moveBranch = (i, dir) => {
    if (readOnly) return;
    const j = i + dir;
    if (j < 0 || j >= branches.length) return;
    const next = [...branches];
    [next[i], next[j]] = [next[j], next[i]];
    setBranches(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8,
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>
            Branches
          </span>
          <button
            type="button"
            onClick={addBranch}
            disabled={readOnly}
            style={addBtnStyle}
            title="Add a parallel branch"
          >
            <Plus size={11} aria-hidden="true" /> Branch
          </button>
        </div>
        <span style={{ fontSize: 10, color: '#64748b', lineHeight: 1.4 }}>
          Each branch runs in parallel. Leave empty to use the default 3-branch
          set (branch1/branch2/branch3).
        </span>

        {branches.length === 0 ? (
          <div style={emptyRow}>
            Using default branches. Click <strong>Branch</strong> to define
            named branches instead.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0,
                       display: 'flex', flexDirection: 'column', gap: 8 }}>
            {branches.map((b, i) => (
              <li
                key={i}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 4,
                  padding: 8,
                  background: '#f8fafc',
                  border: '0.5px solid #e2e8f0',
                  borderRadius: 6,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="text"
                    value={b.id || ''}
                    onChange={(e) => updateBranch(i, 'id', e.target.value)}
                    placeholder="branch_id"
                    disabled={readOnly}
                    autoComplete="off"
                    spellCheck={false}
                    style={{ ...inputStyle, flex: 1,
                             fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11 }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <button type="button" onClick={() => moveBranch(i, -1)} disabled={readOnly || i === 0}
                            title="Move up" style={iconBtnStyle}>
                      <ArrowUp size={11} aria-hidden="true" />
                    </button>
                    <button type="button" onClick={() => moveBranch(i, 1)} disabled={readOnly || i === branches.length - 1}
                            title="Move down" style={iconBtnStyle}>
                      <ArrowDown size={11} aria-hidden="true" />
                    </button>
                    <button type="button" onClick={() => removeBranch(i)} disabled={readOnly}
                            title="Remove branch" style={{ ...iconBtnStyle, color: '#b91c1c' }}>
                      <Trash2 size={11} aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  value={b.label || ''}
                  onChange={(e) => updateBranch(i, 'label', e.target.value)}
                  placeholder="Display label"
                  disabled={readOnly}
                  style={inputStyle}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <div style={{
        padding: 8,
        background: '#f8fafc',
        border: '0.5px dashed #cbd5e1',
        borderRadius: 4,
        fontSize: 10.5, color: '#64748b', lineHeight: 1.5,
      }}>
        Pair this with a <strong>Parallel Join</strong> downstream to wait for
        the branches to complete and merge results. The Join's join-type (all /
        any / N-of-M) controls completion semantics.
      </div>
    </div>
  );
}

/* ───── shared field UI ───────────────────────────────────────────── */

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  fontSize: 12,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  outline: 'none',
  fontFamily: 'inherit',
};

const addBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '3px 8px',
  fontSize: 11, fontWeight: 500,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const iconBtnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 22, height: 22, padding: 0,
  background: 'transparent', border: '0.5px solid transparent',
  borderRadius: 4, color: '#475569', cursor: 'pointer',
};

const emptyRow = {
  padding: '10px 12px',
  background: '#f8fafc',
  border: '0.5px dashed #cbd5e1',
  borderRadius: 6,
  fontSize: 11, color: '#64748b',
  textAlign: 'center',
  lineHeight: 1.5,
};
